<template>
  <v-dialog value="true" scrollable max-width="60vw" persistent>
    <base-modal
      :title="$t('modal.searchReport.title')"
      :confirmEnabled="isValid"
      @confirmed="handleGenerateConfirmation"
      @close="handleClose"
    >
      <div slot="body">
        <v-form v-model="isValid" ref="generateReport" lazy-validation>
          <v-layout row wrap>
            <v-flex xs12 md4>
              <v-text-field
                class="pr-2"
                v-model="form.author"
                :label="$t('modal.searchReport.authorLabel')"
                required
                :rules="rules.requiredText"
              ></v-text-field>
            </v-flex>
            <v-spacer></v-spacer>
            <v-flex xs12 md3>
              <v-menu
                class="pr-2"
                content-class="tds-date-menu"
                v-model="searchDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                full-width
                offset-y
              >
                <v-text-field
                  slot="activator"
                  v-model="generateReportSearchDateToShow"
                  readonly
                  :label="$t('modal.searchReport.searchDateLabel')"
                ></v-text-field>
                <v-date-picker
                  v-model="form.searchDate"
                  no-title scrollable
                  @input="searchDateMenu=false"
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-spacer></v-spacer>
            <v-flex xs12 md3>
              <v-text-field
                v-model="form.hostName"
                :label="$t('modal.searchReport.hostNameLabel')"
                :rules="rules.requiredText"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-text-field
            :label="$t('modal.searchReport.summaryLabel')"
            v-model="form.summary"
            textarea
            :rows="5"
            multi-line
            hide-details
            required
            :rules="rules.requiredText"
          ></v-text-field>
          <div class="section-title-container"><span class="section-title">{{$t('modal.searchReport.searchRangeLabel') }}</span></div>
          <v-layout row wrap>
            <v-flex xs12 md3>
              <v-menu
                class="pr-2"
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                full-width
                offset-y
              >
                <v-text-field
                  slot="activator"
                  v-model="generateReportStartDateToShow"
                  :label="$t('modal.searchReport.startDateLabel')"
                  required
                  :rules="rules.startBeforeEqualEnd"
                  @blur="form.startDate = parseYearMonthDate(formattedStartDate)"
                ></v-text-field>
                <v-date-picker
                  v-model="form.startDate"
                  no-title scrollable
                  type="month"
                  @input="startDateMenu=false; $refs.generateReport.validate()"
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-spacer></v-spacer>
            <v-flex xs12 md3>
              <v-menu
                class="pl-2"
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                full-width
                offset-y
              >
                <v-text-field
                  slot="activator"
                  v-model="generateReportEndDateToShow"
                  :label="$t('modal.searchReport.endDateLabel')"
                  required
                  :rules="rules.endAfterEqualStart"
                  @blur="form.endDate = parseYearMonthDate(formattedEndDate)"
                ></v-text-field>
                <v-date-picker
                  v-model="form.endDate"
                  no-title scrollable
                  type="month"
                  @input="endDateMenu=false; $refs.generateReport.validate()"
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-spacer></v-spacer>
            <v-flex xs12 md4>
              <v-text-field
                v-model="form.language"
                :label="$t('modal.searchReport.languageRestrictionsLabel')"
                required
                :rules="rules.requiredText"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <div class="section-title-container"><span class="section-title">{{ $t('modal.searchReport.sourcesSearchedLabel') }}</span></div>
          <v-layout row wrap>
            <v-flex xs4 v-for="(item, index) in engines" :key="index">
              <v-checkbox
                slot="activator"
                :label="item.name"
                v-model="form.chosenDatabases"
                :value="item.name"
                hide-details
              ></v-checkbox>
            </v-flex>
          </v-layout>

          <div class="section-title-container"><span class="section-title">{{ $t('modal.searchReport.searchStrategyLabel') }}</span></div>
          <div class="copiable-content-container canCopy">
            <v-tooltip bottom class="nav-btn-tooltip-container" id="nav-btn-undo">
              <span slot="activator" @click="handleCopyClick('query')" class="button btn-container">
                <span class="canCopy-text">{{ booleanString }}</span>
                <v-icon slot="activator" class="copy-btn">content_copy</v-icon>
              </span>
              <span>{{ !queryStringSuccessfullyCopied ? $t('modal.searchReport.copySearchStrategy') : $t('modal.searchReport.searchStrategyCopied') }}</span>
            </v-tooltip>
          </div>

          <div class="section-title-container" id="title-shareable-link"><span class="section-title">{{ $t('modal.searchReport.shareLinkLabel') }}</span></div>
          <div v-if="showShareLink" class="copiable-content-container canCopy">
            <v-tooltip bottom class="nav-btn-tooltip-container" id="nav-btn-undo">
              <span slot="activator" @click="handleCopyClick('link')" class="button btn-container">
                <span class="canCopy-text">{{ shareUrl }}</span>
                <v-icon slot="activator" class="copy-btn">content_copy</v-icon>
              </span>
              <span>{{ !shareLinkSuccessfullyCopied ? $t('modal.searchReport.copyLink') : $t('modal.searchReport.linkCopied') }}</span>
            </v-tooltip>
          </div>
          <div v-else class="copiable-content-container cantCopy">
            <span class="noShareLink">{{ $t('modal.searchReport.noShareLink') }}</span>
          </div>
        </v-form>
      </div>
    </base-modal>
  </v-dialog>

</template>

<script>
import BaseModal from '../modals/BaseModal'
import '../../style/_variables.scss'

export default {
  name: 'GenerateReportModal',
  components: {
    BaseModal
  },
  props: ['title', 'value', 'author', 'summary', 'databaseName', 'engines', 'graphRef', 'showShareLink', 'shareUrl'],
  data () {
    return {
      startDateMenu: false,
      endDateMenu: false,
      searchDateMenu: false,
      formattedStartDate: null,
      formattedEndDate: null,
      form: {
        author: this.author,
        startDate: new Date('01 January 1800').toISOString().substr(0, 10),
        endDate: new Date().toISOString().substr(0, 10),
        summary: this.summary,
        hostName: '2Dsearch',
        databaseName: null,
        chosenDatabases: [this.databaseName !== '' ? this.databaseName : null],
        language: 'English',
        searchDate: new Date().toISOString().substr(0, 10)
      },
      queryStringSuccessfullyCopied: false,
      shareLinkSuccessfullyCopied: false,
      isValid: false,
      rules: {
        requiredText: [
          v => !!v || this.$t('modal.searchReport.valueRequired')
        ],
        startBeforeEqualEnd: [
          v => {
            if (Date.create(this.form.startDate) <= Date.create(this.form.endDate)) {
              return true
            } else {
              return this.$t('modal.searchReport.startDateValidation')
            }
          }
        ],
        endAfterEqualStart: [
          v => {
            if (Date.create(this.form.startDate) <= Date.create(this.form.endDate)) {
              return true
            } else {
              return this.$t('modal.searchReport.endDateValidation')
            }
          }
        ]
      }
    }
  },
  watch: {
    value: function (val, oldVal) {
      if (val) {
        this.form.author = this.author
        this.form.startDate = '1800-01'
        this.form.endDate = new Date().toISOString().substr(0, 7)
        this.form.summary = this.summary
        this.form.hostName = '2Dsearch'
        this.form.chosenDatabases = [this.databaseName]
        this.form.language = 'English only'
        this.form.searchDate = new Date().toISOString().substr(0, 10)
        this.$refs.generateReport.validate()
      }
    },
    'form.startDate': function () {
      this.formattedStartDate = this.isoToFriendlyMonthYear(this.form.startDate)
      this.$refs.generateReport.validate()
    },
    'form.endDate': function () {
      this.formattedEndDate = this.isoToFriendlyMonthYear(this.form.endDate)
      this.$refs.generateReport.validate()
    },
    'queryStringSuccessfullyCopied': function () {
      this.shareLinkSuccessfullyCopied = false
    },
    'shareLinkSuccessfullyCopied': function () {
      this.queryStringSuccessfullyCopied = false
    }
  },
  methods: {
    handleGenerateConfirmation () {
      if (!this.$refs.generateReport.validate() || this.form.chosenDatabases.length < 1) {
        alert('invalid')
        return
      }
      let body = `${this.$t('searchReport.title')}
      =====================================================================================================
${this.$t('searchReport.authorHeader')} ${this.form.author}
${this.$t('searchReport.dateHeader')} ${this.generateReportSearchDateToShow}
${this.$t('searchReport.hostHeader')} ${this.form.hostName}

${this.$t('searchReport.summaryHeader')} ${this.form.summary}
${this.$t('searchReport.periodHeader')} ${this.generateReportStartDateToShow} - ${this.generateReportEndDateToShow}
${this.$t('searchReport.languageHeader')} ${this.form.language}

${this.$tc('searchReport.sourcesUsedLine', this.form.chosenDatabases.length, {amount: this.form.chosenDatabases.length})}
`
      this.form.chosenDatabases.forEach(databaseName => {
        body += ` - ${databaseName} ${this.$t('searchReport.databaseLine')} ${this.form.hostName}
`
      })
      body += `
${this.$t('searchReport.searchStrategyLine', {dbName: this.databaseName})}
${this.booleanString}

${this.showShareLink ? `${this.$t('searchReport.shareLinkLine')}
${this.shareUrl}` : ''}`
      this.$emit('confirmed', [body, this.form.searchDate])
    },
    isoToFriendlyDayMonthYear (sourceDate) {
      if (sourceDate) {
        const [year, month, day] = sourceDate.split('-')
        return `${day}/${month}/${year}`
      } else {
        return null
      }
    },
    isoToFriendlyMonthYear (sourceDate) {
      if (sourceDate) {
        const [year, month] = sourceDate.split('-')
        return month ? `${month}/${year}` : `${year}`
      } else {
        return null
      }
    },
    parseYearMonthDate (sourceDate) {
      if (sourceDate) {
        const [month, year] = sourceDate.split('/')
        return year ? `${year}-${month.padStart(2, '0')}` : `${month}`
      } else {
        return null
      }
    },
    handleCopyClick (option) {
      switch (option) {
        case 'link':
          navigator.clipboard.writeText(this.shareUrl)
          this.shareLinkSuccessfullyCopied = true
          break
        case 'query':
          navigator.clipboard.writeText(this.booleanString)
          this.queryStringSuccessfullyCopied = true
          break
      }
    },
    handleClose () {
      this.$store.commit('setShowGenerateSearchReport', false)
    }
  },
  computed: {
    generateReportStartDateToShow: function () {
      return this.isoToFriendlyMonthYear(this.form.startDate)
    },
    generateReportEndDateToShow: function () {
      return this.isoToFriendlyMonthYear(this.form.endDate)
    },
    generateReportSearchDateToShow: function () {
      return this.isoToFriendlyDayMonthYear(this.form.searchDate)
    },
    booleanString: { get () { return this.$store.getters.getBooleanString } }
  }
}
</script>

<style lang="scss" scoped>
.section-title-container {
  display: flex;
}
.section-title {
  text-align: left;
  margin: $length-small-xl 0 0 0;
  font-size: $font-size-med;
}
span {
  font-size: $font-size-small;
  margin-bottom: 0;
}
#title-shareable-link {
  margin-top: 15px;
}
.copiable-content-container {
  border: 2px solid $grey-700;
  border-radius: $length-med-xs;
}
.canCopy:hover {
  box-shadow: inset 3px 3px grey;
}
.copy-btn {
  margin-right: 2px;
}
.copy-btn::after {
  content: '';
  position: absolute;
  height: 100%;
  width: 31px;
  background: $grey-800;
  opacity: 0.2;
  border-radius: 0 9px 9px 0;
  margin-left: -2px
}
.canCopy-text {
  padding-bottom: 0;
  text-align: left;
  margin: 5px;
}
.cantCopy {
  display: flex;
}
.noShareLink {
  text-align: left;
  font-style: italic;
  color: $grey-600;
  margin: 5px;
}
.btn-container {
  display: flex;
  justify-content: space-between;
}
.tds-date-menu {
  box-shadow: none;
}
.tds-textarea .input-group__input {
  border-color: $grey-200 !important;
}
.tds-label {
  opacity: 0.54;
}
</style>



// WEBPACK FOOTER //
// src/components/mainMenu/GenerateSearchReportModal.vue